import { useCallback } from 'react';

import { useI18nContext } from './I18nProvider';
import { type TranslationVars, interpolate } from './interpolate';
import { DEFAULT_UI_LANGUAGE } from './languages';
import { ALL_MESSAGES, type Messages } from './messages';

type LeafPaths<T, Prefix extends string = ''> = T extends string
  ? Prefix
  : T extends object
    ? {
        [K in keyof T & string]: LeafPaths<
          T[K],
          Prefix extends '' ? K : `${Prefix}.${K}`
        >;
      }[keyof T & string]
    : never;

/**
 * String-literal union of every dotted path that resolves to a string value
 * in the canonical {@link Messages} shape, e.g. `'thumbs.startRecording'`
 * or `'appointment.transcript.empty'`. Used as the parameter type for `t()` so
 * call sites get autocomplete and adding a key in `en-US.ts` immediately
 * makes it available without further type plumbing.
 */
export type MessageKey = LeafPaths<Messages>;

/**
 * Returns `{ t, language }` for the currently-active locale.
 *
 * `t(key, vars?)` looks up `key` (a dotted path into the message bundle, e.g.
 * `'thumbs.startRecording'`) in the active locale's bundle, interpolates any
 * `{name}`-style placeholders against `vars`, and returns the resulting
 * string. `language` is the current {@link UiLanguageCode}; useful when the
 * caller needs to fork render output by locale beyond what `t` covers.
 *
 * Fallback behaviour:
 * - If `key` resolves to a non-string (the entry is missing from the active
 *   locale), `t` returns the key itself so missing translations are visible
 *   in the UI instead of silently breaking layout.
 * - When no `<I18nProvider>` is mounted, the hook reads from the en-US
 *   bundle via the fallback context, so consumers in trees that haven't
 *   adopted the provider yet render the original English strings.
 *
 * @example
 *   const { t } = useTranslation();
 *   t('thumbs.startRecording'); // → "Start recording"
 *   t('appointment.completed.syncedWithDate', { date: '2026-05-11' });
 */
export function useTranslation() {
  const { language: contextLanguage } = useI18nContext();
  const language =
    contextLanguage in ALL_MESSAGES ? contextLanguage : DEFAULT_UI_LANGUAGE;
  const messages = ALL_MESSAGES[language];

  const t = useCallback(
    (key: MessageKey, vars?: TranslationVars): string => {
      const value = key
        .split('.')
        .reduce<unknown>(
          (acc, segment) =>
            typeof acc === 'object' && acc !== null
              ? (acc as Record<string, unknown>)[segment]
              : undefined,
          messages,
        );
      if (typeof value !== 'string') return key;
      return vars ? interpolate(value, vars) : value;
    },
    [messages],
  );

  return { t, language };
}
